import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Badge, Spinner, Modal } from 'react-bootstrap';
import { LoadingState } from '../../components/common/LoadingState';
import axiosInstance from '../../api/axios';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DispatchManager = () => {
    const { t } = useTranslation();
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(null);
    const [confirming, setConfirming] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedZone, setSelectedZone] = useState(null);

    const loadRoutes = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/warehouse/routes');
            setRoutes(res.data.data || []);
        } catch (error) {
            console.error('Error loading routes', error);
            toast.error(t('error_loading_routes', 'Error al cargar las rutas de despacho'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRoutes();
    }, []);

    const handleGeneratePdf = async (zoneId) => {
        setGenerating(zoneId);
        try {
            const res = await axiosInstance.post(`/warehouse/routes/${zoneId}/pdf`, {}, {
                responseType: 'blob'
            });

            // Create a blob URL and trigger download
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            
            // Open PDF in new tab for preview
            window.open(url, '_blank');
            
            toast.success(t('load_sheet_generated_success', 'Hoja de carga generada — revísala en la nueva pestaña'));
        } catch (error) {
            console.error('Error generating PDF', error);
            if (error.response?.status === 404) {
                toast.warning(t('no_paid_orders_warning', 'No hay órdenes pagadas para despachar en esta zona'));
            } else {
                toast.error(t('error_generating_pdf', 'Error al generar la hoja de carga'));
            }
        } finally {
            setGenerating(null);
        }
    };

    const openConfirmModal = (route) => {
        setSelectedZone(route);
        setShowConfirmModal(true);
    };

    const handleConfirmDispatch = async () => {
        if (!selectedZone) return;
        setConfirming(selectedZone.zone_id);
        try {
            const res = await axiosInstance.post(`/warehouse/routes/${selectedZone.zone_id}/confirm`);
            toast.success(res.data.message || t('dispatch_confirmed_success', 'Despacho confirmado'));
            setShowConfirmModal(false);
            setSelectedZone(null);
            await loadRoutes();
        } catch (error) {
            console.error('Error confirming dispatch', error);
            toast.error(error.response?.data?.message || t('error_confirming_dispatch', 'Error al confirmar el despacho'));
        } finally {
            setConfirming(null);
        }
    };

    return (
        <div>
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="card-title mb-0">
                    <i className="mdi mdi-truck-delivery me-2"></i>
                    {t('dispatch_sheets_title', 'Hojas de Carga y Despachos')}
                </h4>
                <Button variant="outline-primary" onClick={loadRoutes} disabled={loading}>
                    <i className="mdi mdi-refresh me-1"></i> {t('btn_refresh', 'Actualizar')}
                </Button>
            </div>

            {loading ? (
                <div className="d-flex justify-content-center my-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : routes.length === 0 ? (
                <Card>
                    <Card.Body className="text-center py-5">
                        <i className="mdi mdi-truck-check text-success" style={{ fontSize: '48px' }}></i>
                        <h5 className="mt-3">{t('all_caught_up', 'Todo al día')}</h5>
                        <p className="text-muted">{t('no_paid_orders_dispatch', 'No hay pedidos pagados pendientes por despachar en ninguna zona.')}</p>
                    </Card.Body>
                </Card>
            ) : (
                <Row>
                    {routes.map(route => (
                        <Col md={6} lg={4} key={route.zone_id} className="mb-4">
                            <Card className="h-100 shadow-sm border-0" style={{ transition: 'all 0.3s ease' }}
                                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <Badge bg="info" className="mb-2">{route.zone_code}</Badge>
                                            <h5 className="mb-0 fw-bold">{route.zone_name}</h5>
                                        </div>
                                        <div className="bg-light rounded p-2 text-center" style={{ minWidth: '70px' }}>
                                            <h3 className="mb-0 text-primary fw-bold">{route.total_orders}</h3>
                                            <small className="text-muted">{t('orders_count_badge', 'Pedidos')}</small>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <p className="mb-1 text-muted">{t('total_amount_dispatch', 'Monto Total a Despachar:')}</p>
                                        <h4 className="text-success mb-0">
                                            ${parseFloat(route.total_value).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </h4>
                                    </div>

                                    <div className="mt-auto d-grid gap-2">
                                        <Button
                                            variant="outline-dark"
                                            disabled={generating === route.zone_id}
                                            onClick={() => handleGeneratePdf(route.zone_id)}
                                        >
                                            {generating === route.zone_id ? (
                                                 <><Spinner size="sm" animation="border" className="me-2" /> {t('generating_label', 'Generando...')}</>
                                            ) : (
                                                <><i className="mdi mdi-printer me-1"></i> {t('btn_print_route_sheet', 'Imprimir Hoja de Ruta')}</>
                                            )}
                                        </Button>
                                        <Button
                                            variant="success"
                                            onClick={() => openConfirmModal(route)}
                                        >
                                            <i className="mdi mdi-check-circle me-1"></i> {t('btn_confirm_dispatch', 'Confirmar Despacho')}
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('modal_confirm_dispatch_title', 'Confirmar Despacho')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedZone && (
                        <div>
                            <p>{t('confirm_dispatch_question', '¿Estás seguro de marcar como Despachado todas las órdenes de la zona:')}</p>
                            <div className="bg-light p-3 rounded mb-3 text-center">
                                <h5 className="mb-1">{selectedZone.zone_name}</h5>
                                <Badge bg="info">{selectedZone.zone_code}</Badge>
                                <div className="mt-2">
                                    <strong>{selectedZone.total_orders}</strong> {t('orders_count_label', 'pedidos')} por <strong>${parseFloat(selectedZone.total_value).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                                </div>
                            </div>
                            <p className="text-muted small">
                                <i className="mdi mdi-information me-1"></i>
                                {t('irreversible_action_warning', 'Esta acción es irreversible. Asegúrate de haber impreso la hoja de ruta antes de confirmar.')}
                            </p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        {t('btn_cancel', 'Cancelar')}
                    </Button>
                    <Button
                        variant="success"
                        disabled={confirming !== null}
                        onClick={handleConfirmDispatch}
                    >
                        {confirming ? (
                            <><Spinner size="sm" animation="border" className="me-2" /> {t('processing_label', 'Procesando...')}</>
                        ) : (
                            <><i className="mdi mdi-check-circle me-1"></i> {t('btn_yes_confirm_dispatch', 'Sí, Confirmar Despacho')}</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DispatchManager;
